import { ReactElement, ReactNode } from "react";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { GridColumnCount } from "models/utilTypes/uiTypes";
import { ComponentBaseProps } from "models/propTypes/basePropTypes";
import {
  BaseFormComponentProps,
  BaseFormViewComponentProps,
  CustomFormFieldsProps,
  FormFieldDefinitionType,
} from "models/propTypes/genericFormProps";
import { MoreActionsBaseProps } from "models/propTypes/commonPropTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FormikProps } from "formik";
import { ProductPeriodisation, SendMailOptions } from "models/formTypes/invoice";
import { StyleEnums } from "models/enums";
import { ImageMeta } from "./fileViewerPropTypes";
import { InvoiceProductTabs } from "models/enums/invoice";
import { NestedKeyOf } from "models/utilTypes/nestedKeyType";
import {
  BaseFormFieldProps,
  SelectInputItemProps,
  WintFormConnect,
  WintFormErrors,
  WintFormSetValues,
  WintFormTouched,
  WizardProps,
} from "./wintFormPropsTypes";
import { ObjectSchema } from "models/utilTypes/yupSchemaTypes";
import { ForwardableCost } from "models/utilTypes/costForwardingTypes";
import { ForwardedCostsSelection, ForwardedCostsSummary } from "models/utilTypes/forwardableCostTypes";

export interface InvoiceListViewProps {
  filter?: InvoiceFormTypes.Filter;
  reloadKey?: number;
  includeSummary?: boolean;
  tableKey?: string;
  /** File name to be used when exporting table content */
  exportFileName?: string;
}

export interface InvoiceCreateProps {
  invoice?: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice;
  isRecurring?: boolean;
  reloadInvoice?: () => void;
  loading?: boolean;
  additionalImages?: ImageMeta[];
}

export interface InvoiceWizardQuickSelectionProps {
  connect: WintFormConnect<InvoiceFormTypes.Create>;
  values: InvoiceFormTypes.Create;
  isRecurring?: boolean;
  invoice?: WintSuperkollAPIModel.RecurringInvoice | WintSuperkollAPIModel.Invoice;
}

export interface InvoiceDetailsViewProps {
  invoice?: WintSuperkollAPIModel.Invoice;
  reloadInvoice?: () => void;
}

export interface RecurringInvoiceDetailsViewProps {
  invoice?: WintSuperkollAPIModel.RecurringInvoice;
  reloadInvoice?: () => void;
}

export interface RecurringDetailsProps {
  invoice?: WintSuperkollAPIModel.RecurringInvoice;
  displayType?: StyleEnums.DisplayType;
}

export interface InvoiceFormProps extends BaseFormViewComponentProps {
  hasROT: boolean;
  hasRUT: boolean;
  /** See CustomerSelectorProps.revenueAccountsEnabled. */
  revenueAccountsEnabled?: boolean;
  invoice?: WintSuperkollAPIModel.RecurringInvoice | WintSuperkollAPIModel.Invoice;
  isRecurring: boolean;
  reloadInvoice: (invoiceId: number) => void;
  showOnlyRecurringFields?: boolean;
  additionalImages?: ImageMeta[];
}

export interface QuotationFormProps extends BaseFormViewComponentProps {
  quotation?: WintSuperkollAPIModel.Quotation;
  hasROT: boolean;
  hasRUT: boolean;
}

export interface InvoiceFormContainerProps extends BaseFormComponentProps {
  invoice?: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice;
  isRecurring: boolean;
  reloadInvoice: (invoiceId: number) => void;
  showOnlyRecurringFields?: boolean;
  additionalImages?: ImageMeta[];
}

export interface CreateInvoiceFooterProps {
  isRecurring: boolean;
  invoice?: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice;
  form: FormikProps<InvoiceFormTypes.Create>;
  reloadInvoice: (invoiceId: number) => void;
  wizard: WizardProps;
  revenueAccountsEnabled?: boolean;
  forwardedCosts?: ForwardedCostsFooterActions;
}

export interface InvoiceEditDueFormContainerProps extends BaseFormComponentProps {
  invoiceId: number;
  dueDate: Date;
  invoiceDate: Date;
  reminderDueDate?: Date;
}

export interface InvoiceEditDueFormViewProps extends BaseFormViewComponentProps {
  invoiceDate: Date;
}

export interface InvoiceReportAsPaidFormContainerProps extends BaseFormComponentProps {
  invoiceId: number;
  paymentDate?: Date;
}

export interface InvoiceReportAsPaidFormViewProps extends BaseFormViewComponentProps {}

export interface InvoiceReferencePersonFormFields {
  name: string;
  mail: string;
  telephone: string;
}

export interface InvoiceReferencePersonFormProps {
  customerId: number;
}

export interface InvoiceSendMailContainerProps extends BaseFormComponentProps {
  invoiceId?: number;
  customerId: number;
  form: WintSuperkollAPIModel.InvoiceDefaultMailOptions;
  referencePersonName?: string;
  actionBeforeSubmit?: () => Promise<number | undefined>;
  warnings?: WintSuperkollAPIModel.Warning[];
  status?: WintSuperkollAPIModel.InvoiceStatus;
  handleSubmit?: (values: any) => Promise<any>;
  isQuotation?: boolean;
}

export interface InvoiceSendMailViewProps extends BaseFormViewComponentProps {
  invoiceId?: number;
  customerId: number;
  form: WintSuperkollAPIModel.InvoiceDefaultMailOptions;
  referencePersonName?: string;
  serialNumberTemplateValueDisplayText: string;
  warnings?: WintSuperkollAPIModel.Warning[];
  status?: WintSuperkollAPIModel.InvoiceStatus;
  isQuotation?: boolean;
}

export interface InvoicePaymentReminderFormViewProps extends BaseFormViewComponentProps {
  currency?: string;
  reminderDateChangeHandler: (name: string, updatedValue: Date, formikProps: FormikProps<any>) => void;
  invoiceSendMethod?: WintSuperkollAPIModel.InvoiceSendMethod;
}

export interface InvoicePaymentReminderFormContainerProps extends BaseFormComponentProps {
  invoice: WintSuperkollAPIModel.Invoice;
}

export interface InvoiceFooterProps {
  isValid?: boolean;
  sendType?: WintSuperkollAPIModel.InvoiceSendMethod;
  invoice: WintSuperkollAPIModel.Invoice;
  formToAPIHandler?: () => Promise<WintSuperkollAPIModel.InvoicePut | undefined>;
  createOrUpdateInvoice?: () => Promise<number>;
  reloadHandler?: () => void;
  markAsDoneHandler?: () => void;
  columnsLeft?: GridColumnCount;
  hideSave?: boolean;
  isComplete?: boolean;
  isFinalPreview?: boolean;
  onSendInvoice?: () => Promise<any>;
  onShowFinalApproval?: () => void;
  onShowReview?: () => void;
  disableSuccessMessages?: boolean;
  setLoading?: (loading: boolean) => void;
}

export interface RecurringInvoiceFooterProps {
  isValid: boolean;
  invoice: WintSuperkollAPIModel.RecurringInvoice;
  createOrUpdateInvoice?: () => Promise<number>;
  reloadHandler?: () => void;
  markAsDoneHandler?: () => void;
  columnsLeft?: GridColumnCount;
}

export type RecurringInvoiceMoreActionProps = {
  invoice: WintSuperkollAPIModel.RecurringInvoice;
  reloadHandler?: () => void;
  markAsDoneHandler?: () => void;
};

export type InvoiceMoreActionProps = {
  invoice: WintSuperkollAPIModel.Invoice;
  reloadHandler?: () => void;
  markAsDoneHandler?: () => void;
  disableSuccessMessages?: boolean;
  setLoading?: (loading: boolean) => void;
};

export interface InvoiceWizardMoreActionProps extends MoreActionsBaseProps {
  invoiceId?: number;
  invoiceOwnerId?: number;
  createInvoiceHandler: (validationSchema?: ObjectSchema<InvoiceFormTypes.Create>, asDraft?: boolean) => Promise<void>;
  reloadInvoiceHandler: (invoiceId: number) => void;
  isQuotation?: boolean;
  forwardedCosts?: ForwardedCostsFooterActions;
}

export interface InvoiceLayoutSelectorProps extends ComponentBaseProps, CustomFormFieldsProps {
  value?: WintSuperkollAPIModel.InvoicePdfTemplate;
  onChange: (selectedType: WintSuperkollAPIModel.InvoicePdfTemplate) => void;
  onBlur: () => void;
  disabled?: boolean;
}

export interface RecurringPeriodisationProps extends BaseFormFieldProps {
  value: InvoiceFormTypes.RecurringPeriodisation;
  disabled?: boolean;
  isPeriodisationAdvancedConfiguration: boolean;
  recurringInvoiceStartDate?: Date;
  recurringInvoiceEndDate?: Date;
  recurringInvoiceFrequency?: WintSuperkollAPIModel.RecurringInvoiceFrequency;
  stacked?: boolean;
  onInputFocus?: () => void;
}

export interface PeriodisationProps extends BaseFormFieldProps {
  value: InvoiceFormTypes.InvoicePeriodisation;
  fromRecurring?: boolean;
  disabled?: boolean;
  disableModeToggle?: boolean;
  lockedTooltipText?: string;
}

export interface InvoiceServicesSelectorProps {
  value?: InvoiceFormTypes.Product[] | null;
  onChange: (products: InvoiceFormTypes.Product[]) => void;
  name?: string;
  innerFields?: FormFieldDefinitionType<any>;
  formikProps?: FormikProps<any>;
  touched?: any;
  errors?: any;
}

export interface InvoiceCreateContextState {
  invoiceHtml?: string;
  invoiceDate?: Date;
  deliveryDate?: Date;
  recurringStartDate?: Date;
  recurringFrequency?: WintSuperkollAPIModel.RecurringInvoiceFrequency;
  setInvoiceHtml: (content: string) => void;
  setInvoiceDate: (date?: Date) => void;
  setDeliveryDate: (date?: Date) => void;
  setRecurringStartDate: (date?: Date) => void;
  setRecurringFrequency: (value?: WintSuperkollAPIModel.RecurringInvoiceFrequency) => void;
}

export interface PeriodisationSummaryProps {
  value: ProductPeriodisation[];
  onEditClick?: () => void;
  showHeaders?: boolean;
  editButtonLabel?: string;
}

export interface EditPeriodisationRowFormValue {
  invoiceItemId: number;
  text: string;
  periodisation: InvoiceFormTypes.InvoicePeriodisation;
}

export interface EditPeriodisationFormType {
  rows: EditPeriodisationRowFormValue[];
  isPeriodisationAdvancedConfiguration: boolean;
  isPeriodisationByDate: boolean;
}

export interface EditPeriodisationDialogProps extends BaseFormComponentProps {
  invoice: WintSuperkollAPIModel.Invoice;
  reloadInvoice?: () => void;
  fileViewer?: ReactElement;
}

export interface PeriodisationEditorProps {
  values: EditPeriodisationFormType;
  connect: WintFormConnect<EditPeriodisationFormType>;
  setValues: WintFormSetValues<EditPeriodisationFormType>;
  lockedInvoiceItemIds: Set<number>;
}

export interface DimensionsSummaryProps {
  value: WintSuperkollAPIModel.DimensionData[];
  amount?: number;
  currency?: string;
}

export enum InvoiceTodoView {
  None,
  BaseForm,
  PreviewForm,
}

export interface InvoiceTodoSelectionFormViewProps extends BaseFormViewComponentProps {
  invoice: WintSuperkollAPIModel.Invoice;
  invoiceDateChangeHandler?: (date: Date) => void;
  onInvoiceInitialValidationChange: (valid: boolean) => void;
  onInvoiceSendMethodSelected: (sendType?: WintSuperkollAPIModel.InvoiceSendMethod) => void;
  view?: InvoiceTodoView;
  nextInvoiceSerialNumber?: string;
}

export interface InvoiceTodoSelectionFormContainerProps extends BaseFormComponentProps {
  invoice: WintSuperkollAPIModel.Invoice;
  invoiceDateChangeHandler?: (date: Date) => void;
  onInvoiceInitialValidationChange: (valid: boolean) => void;
  onInvoiceSendMethodSelected: (sendType?: WintSuperkollAPIModel.InvoiceSendMethod) => void;
  view?: InvoiceTodoView;
}

export interface InvoiceDetailsFormContainerProps extends BaseFormComponentProps {
  invoice: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice;
  isRecurring?: boolean;
  loadInvoice?: () => void;
  attachments: ImageMeta[];
  fileViewer?: ReactElement;
}

export interface InvoiceDetailsFormViewProps extends BaseFormViewComponentProps {
  invoice: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice;
  currency: string;
  dimensionAmount: number;
  isReadOnly: boolean;
  isRecurring: boolean;
  isAutomaticRemindersFeatureEnabled: boolean;
  attachments: ImageMeta[];
  loadInvoice?: () => void;
  fileViewer?: ReactElement;
}

export interface InvoiceSectionProps {
  values: InvoiceFormTypes.Details;
}

export interface InvoiceGeneralSectionProps extends InvoiceSectionProps {
  invoice: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice;
}

export interface InvoiceAmountSectionProps extends InvoiceSectionProps {
  currency: string;
}

export interface InvoiceReminderSectionProps extends InvoiceSectionProps {
  invoice: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice;
  isRecurring: boolean;
  isAutomaticRemindersFeatureEnabled: boolean;
}

export interface InvoicePaymentSectionProps extends InvoiceSectionProps {
  invoice: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice;
}

export interface InvoiceDimensionSectionProps extends InvoiceSectionProps {
  invoice: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice;
  currency: string;
  dimensionAmount: number;
  connect: WintFormConnect<InvoiceFormTypes.Details>;
}

export interface InvoicePeriodSectionProps extends InvoiceSectionProps {
  invoice: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice;
  isRecurring: boolean;
  loadInvoice?: () => void;
  fileViewer?: ReactElement;
}

export interface InvoiceVouchersProps {
  id: number;
}

export interface InvoiceSimilarListProps {
  id: number;
}

export interface InvoiceStatusTagsProps {
  status: WintSuperkollAPIModel.InvoiceStatus;
  creditStatus?: WintSuperkollAPIModel.InvoiceCreditStatus;
  reminderInvoicesCount?: number;
  rot?: boolean;
  rut?: boolean;
  houseWorkApplicationIsFiled?: boolean;
  hasUnbookedHouseWorkCases?: boolean;
}

export interface InvoicePaymentStatusProps {
  status: WintSuperkollAPIModel.InvoicePaymentState;
}

export interface InvoiceStateFlowProps {
  invoiceId: number;
  status: WintSuperkollAPIModel.InvoicePaymentState;
}

export interface InvoiceOverdueBannerProps {
  invoice: WintSuperkollAPIModel.Invoice;
  reloadHandler?: () => void;
}

export interface ReminderFeeNotPaidBannerProps {
  invoice: WintSuperkollAPIModel.Invoice;
  reloadHandler?: () => void;
}

export interface AutomaticReminderBannerProps {
  invoiceId: number;
  reloadHandler?: () => void;
}

export interface InvoiceAlertsProps {
  invoice: WintSuperkollAPIModel.Invoice;
}

export interface InvoiceCommonAlertsProps {
  warnings?: WintSuperkollAPIModel.Warning[];
}

export interface InvoiceMailAlertsProps {
  warnings?: WintSuperkollAPIModel.Warning[];
  status?: WintSuperkollAPIModel.InvoiceStatus;
}

export interface InvoicePrintProps {
  invoiceId?: number;
  onFormValidationChange?: (isValid: boolean) => void;
  warnings?: WintSuperkollAPIModel.Warning[];
}

export interface InvoiceTodoDetailsProps {
  invoice: WintSuperkollAPIModel.Invoice;
}

export interface FromRecurringInvoiceBannerProps {
  invoice: WintSuperkollAPIModel.Invoice;
  displayType: StyleEnums.DisplayType;
}

export interface RecurringInvoiceBannerProps {
  invoice: WintSuperkollAPIModel.RecurringInvoice;
  loadInvoice: () => void;
}

export interface RecurringInvoiceStateFlowProps {
  invoiceId: number;
}

export interface RecurringInvoiceStatusTagsProps {
  status: WintSuperkollAPIModel.RecurringInvoiceState;
}

export interface RecurringInvoiceSendTypeProps {
  type: WintSuperkollAPIModel.RecurringInvoiceSendType;
}

export interface CreditInvoiceSelectionFormProps extends BaseFormComponentProps {
  invoiceId: number;
  isTimeReportingInvoice: boolean;
  reloadInvoice?: () => void;
}

export interface CreditedInvoiceBannerProps {
  invoice: WintSuperkollAPIModel.Invoice;
  displayType: StyleEnums.DisplayType;
}

export interface CreditInvoiceBannerProps {
  invoice: WintSuperkollAPIModel.Invoice;
  displayType: StyleEnums.DisplayType;
}

export interface ForwardedCostsDialogFormProps extends BaseFormComponentProps {
  costs: ForwardableCost[];
  initialSelection: ForwardedCostsSelection;
  onApply: (selection: ForwardedCostsSelection) => Promise<void>;
}

export interface ForwardedCostsReminderProps {
  show: boolean;
  customerName: string;
  summary: ForwardedCostsSummary;
  onOpen: () => void;
  onClose: () => void;
  children: ReactElement;
}

export interface ForwardedCostsFooterActions {
  canForwardCosts: boolean;
  showReminder: boolean;
  summary: ForwardedCostsSummary;
  customerName: string;
  openDialog: () => void;
  dismissReminder: () => void;
  // Resolves once every cost file being fetched is in the form's values; getLatestValues then returns them.
  waitForAttachments: () => Promise<void>;
  getLatestValues: () => InvoiceFormTypes.Create;
}

export interface CreateInvoiceDefaults {
  productId?: number;
  customer?: WintSuperkollAPIModel.Customer;
}

export interface InvoiceDraftFooterProps {
  invoiceId?: number;
  invoiceOwnerId?: number;
  onShowInvoicePreview: () => Promise<void>;
  submitInvoice: (validationSchema?: ObjectSchema<InvoiceFormTypes.Create>, asDraft?: boolean) => Promise<any>;
  reloadInvoice: (invoiceId: number) => void;
  isFormLoading: boolean;
  wizardProps: WizardProps;
  forwardedCosts?: ForwardedCostsFooterActions;
}

export interface RecurringInvoiceDraftFooterProps {
  invoiceId?: number;
  submitInvoice: () => Promise<any>;
  toggleRecurringInvoiceState: () => Promise<any>;
  reloadInvoice: (invoiceId: number) => void;
  isFormLoading: boolean;
  status: WintSuperkollAPIModel.RecurringInvoiceState;
  wizardProps: WizardProps;
}

export interface InvoicePreviewFormContainerBaseProps {
  invoicePut?: WintSuperkollAPIModel.InvoicePut;
  invoiceId?: number;
  serialNumber?: number;
  ocr?: string;
  status?: WintSuperkollAPIModel.InvoiceStatus;
  initialValues?: InvoiceFormTypes.Preview;
  canSendLastVersionToPagero?: boolean;
  warnings?: WintSuperkollAPIModel.Warning[];
  fromRecurring?: boolean;
  recurringInvoiceVersion?: WintSuperkollAPIModel.RecurringInvoiceVersion;
  recurringId?: number;
  reloadInvoice?: (invoiceId: number) => void;
  currency: string;
  isReminder?: boolean;
  reminderFeesUpdateHandler?: (fees: number) => void;
  version?: number;
  onInvoiceSendMethodSelected?: (sendType?: WintSuperkollAPIModel.InvoiceSendMethod) => void;
  nextInvoiceSerialNumber?: string;
}

export interface InvoicePreviewFormContainerProps
  extends InvoicePreviewFormContainerBaseProps,
    BaseFormComponentProps {}

export interface InvoicePreviewFormProps extends BaseFormViewComponentProps {
  invoicePut?: WintSuperkollAPIModel.InvoicePut;
  invoiceId?: number;
  serialNumber?: number;
  ocr?: string;
  status?: WintSuperkollAPIModel.InvoiceStatus;
  currency: string;
  fromRecurring?: boolean;
  recurringInvoiceVersion?: WintSuperkollAPIModel.RecurringInvoiceVersion;
  recurringId?: number;
  isReminder?: boolean;
  version?: number;
  reminderFeesUpdateHandler?: (fees: number) => void;
  warnings?: WintSuperkollAPIModel.Warning[];
  canSendLastVersionToPagero?: boolean;
  onInvoiceSendMethodSelected?: (sendType?: WintSuperkollAPIModel.InvoiceSendMethod) => void;
  nextInvoiceSerialNumber?: string;
}

export interface InvoiceDetailsProps {
  total: number;
  tax: number;
  currency: string;
  referencePersonName?: string;
  paymentTerms: number;
  invoiceRows: InvoiceFormTypes.Product[] | WintSuperkollAPIModel.InvoiceRow[];
}

export interface InvoicePreviewProps extends InvoicePreviewFormContainerBaseProps, InvoiceDetailsProps {
  customerName: string;
  customerEmail?: string;
  referecePersonEmail?: string;
}

export interface RutRotSummaryProps extends BaseFormFieldProps {
  values: InvoiceFormTypes.Create;
}

export interface SendToDebtCollectionFormContainerProps extends BaseFormComponentProps {
  invoiceId?: number;
}

export interface InvoiceVersionsProps {
  invoice: WintSuperkollAPIModel.Invoice;
}

export interface InvoiceTodoExtended extends WintSuperkollAPIModel.Invoice {
  preview?: string;
  previewLoading?: boolean;
}

export interface CustomerSelectorProps {
  connect: WintFormConnect<InvoiceFormTypes.Create>;
  /**
   * Whether the company has opted in to article revenue accounts (Heart setting, read via
   * GET api/Account/RevenueAccounts). The row VAT lock is strictly opt-in per company (R11);
   * ArticleSearchResponse.Account comes back regardless of the flag, so without this the lock
   * would fire for companies that never enabled the feature.
   */
  revenueAccountsEnabled?: boolean;
  /**
   * Set when the form holds a credit note ("kreditera och redigera"); undefined otherwise, which
   * is always the case for quotations. Passed straight to InvoiceHelper.getRowsNeedingVatReset,
   * which exempts credit rows from the VAT snap-back - see the reasoning there.
   */
  creditedInvoiceId?: number;
  values: InvoiceFormTypes.Create;
  submitted: boolean;
  setValues: WintFormSetValues<InvoiceFormTypes.Create>;
  wizardProps: WizardProps;
  isQuotation: boolean;
  disableCustomerSelection?: boolean;
}

export type InvoiceContentSelectionFormType = InvoiceFormTypes.Create | InvoiceFormTypes.SelfInvoice;

export interface ContentSelectorProps<T> {
  invoiceId?: number;
  /**
   * Whether the company has opted in to article revenue accounts (Heart setting, read via
   * GET api/Account/RevenueAccounts). The row VAT lock is strictly opt-in per company (R11);
   * ArticleSearchResponse.Account comes back regardless of the flag, so without this the lock
   * would fire for companies that never enabled the feature.
   */
  revenueAccountsEnabled?: boolean;
  connect: WintFormConnect<T>;
  values: T;
  errors: WintFormErrors<T>;
  touched: WintFormTouched<T>;
  submitted: boolean;
  wizardProps: WizardProps;
  isRecurring?: boolean;
  isQuotation?: boolean;
  setValues: WintFormSetValues<T>;
  /** Extra ways of adding rows, shown under the article suggestions. */
  additionalRowActions?: ReactNode;
}

export interface RutRotSelectionProps {
  connect: WintFormConnect<InvoiceFormTypes.Create>;
  values: InvoiceFormTypes.Create;
  errors: WintFormErrors<InvoiceFormTypes.Create>;
  touched: WintFormTouched<InvoiceFormTypes.Create>;
  submitted: boolean;
  wizardProps: WizardProps;
  hasROT: boolean;
  hasRUT: boolean;
}

export interface OtherSettingsProps {
  connect: WintFormConnect<InvoiceFormTypes.Create>;
  values: InvoiceFormTypes.Create;
  submitted: boolean;
  wizardProps: WizardProps;
  isRecurring: boolean;
  creditedInvoiceId?: number;
  pendingAttachments?: number;
}

export interface RecurringConfigurationProps extends RecurringConfigurationFieldsProps {
  wizardProps: WizardProps;
  submitted: boolean;
}

export interface RecurringConfigurationFieldsProps {
  connect: WintFormConnect<InvoiceFormTypes.Create>;
  values: InvoiceFormTypes.Create;
  touched: WintFormTouched<InvoiceFormTypes.Create>;
  invoice?: WintSuperkollAPIModel.RecurringInvoice | WintSuperkollAPIModel.Invoice;
}

export interface InvoiceProductRowProps<T> {
  index: number;
  /**
   * Whether the company has opted in to article revenue accounts (Heart setting, read via
   * GET api/Account/RevenueAccounts). The row VAT lock is strictly opt-in per company (R11);
   * ArticleSearchResponse.Account comes back regardless of the flag, so without this the lock
   * would fire for companies that never enabled the feature.
   */
  revenueAccountsEnabled?: boolean;
  dragDropRef: any;
  formValues: T;
  connectToForm: WintFormConnect<T>;
  row: InvoiceFormTypes.Product;
  touched: WintFormTouched<InvoiceFormTypes.Product>;
  errors: WintFormErrors<InvoiceFormTypes.Product>;
  suggestedArticles: AutocompleteListItem[];
  currentTab: InvoiceProductTabs;
  onArticleSearch: (searchText: string) => Promise<never[] | AutocompleteListItem[]>;
  updateRow: (newRow: InvoiceFormTypes.Product, shouldValidate?: boolean) => void;
  onArticleSelect: (value?: AutocompleteListItem) => void;
  onUnitSelect: (value?: number) => void;
  isLastRow: boolean;
  removeRow: (index: number) => void;
  units: SelectInputItemProps[];
  masterRow: boolean;
  rowLevelDimensions: boolean;
  rowLevelPeriodisation: boolean;
  disablePeriodisationModeToggle: boolean;
  masterRowDimensions: string;
  masterRowPeriodisation: string;
  masterRowRecurringPeriodisation: string;
  isRecurring?: boolean;
  isQuotation?: boolean;
  isEUCustomer?: boolean;
  vatLockedToVatFree?: boolean;
  handlerId: any;
  canDrag: boolean;
  isDragging: boolean;
  isOver: boolean;
}

export interface DraggableInvoiceProductRowProps<T>
  extends Omit<InvoiceProductRowProps<T>, "dragDropRef" | "handlerId" | "isDragging" | "isOver"> {
  moveRow: (source: number, target: number) => void;
}

export interface CreateInvoiceTemplatePreviewProps {
  values: InvoiceFormTypes.Create;
  isRecurring: boolean;
  invoice?: WintSuperkollAPIModel.RecurringInvoice | WintSuperkollAPIModel.Invoice;
  additionalImages?: ImageMeta[];
}

export interface InvoiceTemplatePreviewProps {
  values: InvoiceFormTypes.Preview;
  invoicePut?: WintSuperkollAPIModel.InvoicePut;
  invoiceId?: number;
  serialNumber?: number;
  ocr?: string;
  status?: WintSuperkollAPIModel.InvoiceStatus;
  fromRecurring?: boolean;
  recurringInvoiceVersion?: WintSuperkollAPIModel.RecurringInvoiceVersion;
  recurringId?: number;
  isReminder?: boolean;
  version?: number;
}

export interface RutRotSelectorProps {
  connect: WintFormConnect<InvoiceFormTypes.Create>;
  values: InvoiceFormTypes.Create;
  errors: WintFormErrors<InvoiceFormTypes.Create>;
  touched: WintFormTouched<InvoiceFormTypes.Create>;
}

export interface RutRotFieldGroupProps {
  connect: WintFormConnect<InvoiceFormTypes.Create>;
  values: InvoiceFormTypes.Create;
  errors: WintFormErrors<InvoiceFormTypes.Create>;
  touched: WintFormTouched<InvoiceFormTypes.Create>;
  index: number;
  row: InvoiceFormTypes.RutRotArticleItem;
  houseWorkType: WintSuperkollAPIModel.InvoiceHouseWorkType;
  rutRotWorkTypes: SelectInputItemProps[];
}

export interface InvoiceOtherGroupProps {
  connect: WintFormConnect<InvoiceFormTypes.Create>;
  values: InvoiceFormTypes.Create;
  isQuotation?: boolean;
}

export interface MessageGroupProps extends InvoiceOtherGroupProps {
  pendingAttachments?: number;
}

export interface DateGroupProps {
  connect: WintFormConnect<InvoiceFormTypes.Create>;
  values: InvoiceFormTypes.Create;
}

export interface OtherGroupProps extends InvoiceOtherGroupProps {
  paymentOptions?: WintSuperkollAPIModel.InvoicePaymentOption[];
  sellers?: string[];
}

export interface InvoiceReminderGroupProps extends InvoiceOtherGroupProps {
  invoice?: WintSuperkollAPIModel.RecurringInvoice | WintSuperkollAPIModel.Invoice;
}

export interface SendMailFormFieldsProps<T extends Record<string, any>> {
  connect: WintFormConnect<T>;
  parentFieldName?: NestedKeyOf<T> & string;
  invoiceId?: number;
  mailOptions?: SendMailOptions;
  isNotSendForm?: boolean;
  isQuotation?: boolean;
  vertical?: boolean;
  fieldsLoading?: boolean;
}

export interface SendToMailSelectorProps extends BaseFormFieldProps {
  form: FormikProps<any>;
  parentFieldName?: string;
  customerId?: number;
  customerEmail?: string;
  referencePersonEmail?: string;
  mailForm?: WintSuperkollAPIModel.InvoiceDefaultMailOptions;
}

export interface CustomerInformationLoaderProps {
  invoiceId?: number;
  values: InvoiceFormTypes.Create;
  connect: WintFormConnect<InvoiceFormTypes.Create>;
  setValues: WintFormSetValues<InvoiceFormTypes.Create>;
  lastSavedSendProperties?: WintSuperkollAPIModel.InvoiceSendProperties;
  /** See CustomerSelectorProps.revenueAccountsEnabled. */
  revenueAccountsEnabled?: boolean;
  creditedInvoiceId?: number;
}

export interface SendInformationLoaderProps {
  invoiceId?: number;
  version?: number;
  isReminder?: boolean;
  values: InvoiceFormTypes.Preview | InvoiceFormTypes.Todo;
  connect: WintFormConnect<InvoiceFormTypes.Preview | InvoiceFormTypes.Todo>;
  setValues: WintFormSetValues<InvoiceFormTypes.Preview> | WintFormSetValues<InvoiceFormTypes.Todo>;
  nextInvoiceSerialNumber?: string;
  skipSendInformationLoad?: boolean;
}

export interface InvoicePreviewSendOptionSectionProps {
  invoiceId?: number;
  connect: WintFormConnect<InvoiceFormTypes.Preview | InvoiceFormTypes.Todo>;
  setValues: WintFormSetValues<InvoiceFormTypes.Preview> | WintFormSetValues<InvoiceFormTypes.Todo>;
  values: InvoiceFormTypes.Preview | InvoiceFormTypes.Todo;
  version?: number;
  invoicePut?: WintSuperkollAPIModel.InvoicePut;
  isReminder?: boolean;
  canSendLastVersionToPagero?: boolean;
  status?: WintSuperkollAPIModel.InvoiceStatus;
  warnings?: WintSuperkollAPIModel.Warning[];
  noGrouping?: boolean;
  onInvoiceSendMethodSelected?: (sendType?: WintSuperkollAPIModel.InvoiceSendMethod) => void;
  nextInvoiceSerialNumber?: string;
  skipSendInformationLoad?: boolean;
  // Lets a parent that owns the "Skicka som" section place the forwarded-cost banner outside it.
  onWarningsChange?: (warnings?: WintSuperkollAPIModel.Warning[]) => void;
}

export interface ForwardedCostWarningsProps {
  warnings?: WintSuperkollAPIModel.Warning[];
}
